/**
 * GDPR-oriented span sanitization, applied before any span reaches the
 * exporter pipeline.
 *
 * Scope: this app's data-classification model treats content entered by or
 * relating to a customer (tenant/programme/module names, tenant slugs in a
 * URL path, business identifiers) as customer business data, not personal
 * data — customers are expected to enter only public-facing content, and a
 * violation of that expectation is handled operationally (a purge from the
 * Application Insights backend), not architecturally. What this processor
 * actually protects is personal data belonging to an individual — an email
 * address, wherever one turns up in a URL, query string, or exception
 * message — plus query strings generally, since this app has no legitimate
 * reason to export one. GUID-shaped path segments (`/tenants/${tenantId}/users`,
 * src/platform/http-client.ts) are also generalized, but for cardinality
 * hygiene, not because an id is personal data.
 *
 * Registering this processor first (see tracer-provider.ts) means every
 * span is sanitized before the batch processor ever queues it for network
 * export.
 *
 * @module telemetry/privacy-span-processor
 */
import type { Span, SpanProcessor } from "@opentelemetry/sdk-trace-web";

const EMAIL_RE = /[^\s/?#]+@[^\s/?#]+\.[^\s/?#]+/gi;
const GUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const URL_IN_TEXT_RE = /https?:\/\/\S+/gi;

/** Attributes that carry request/dependency URLs across the instrumentation libraries in use. */
const URL_ATTRIBUTES = ["http.url", "http.target", "url.full"] as const;

/** Maximum length kept for a sanitized exception message. */
const EXCEPTION_MESSAGE_LIMIT = 512;

/**
 * Strip query strings entirely and redact email-shaped path segments. Query
 * strings are dropped outright rather than selectively scrubbed: this app
 * has no legitimate reason to export a query string, and an allowlist of
 * "safe" params is one more thing to keep in sync as routes change.
 *
 * GUID-shaped segments are also generalized — not because a tenant, module,
 * or programme id is personal data (it isn't; that content is customer
 * business data, not PII, and is out of scope here), but because a bare
 * GUID gives every span for the same resource a distinct, high-cardinality
 * attribute value with no diagnostic upside over a stable placeholder.
 *
 * @param raw - The URL as captured by an instrumentation library.
 * @returns A sanitized URL, or a fixed placeholder if `raw` cannot be parsed.
 */
export function sanitizeUrl(raw: string): string {
  try {
    const url = new URL(raw, typeof window !== "undefined" ? window.location.origin : undefined);
    url.search = "";
    url.hash = "";
    const path = url.pathname.replace(GUID_RE, ":id").replace(EMAIL_RE, ":redacted");
    return `${url.origin}${path}`;
  } catch {
    return "[unparsable-url]";
  }
}

/**
 * Sanitize free text that may embed a URL (an exception message built from a
 * failed request, for instance) or a bare GUID/email outside of one, then
 * bound its length.
 *
 * Unlike {@link sanitizeUrl}, this does not assume the whole string is a URL
 * — `exception.message` is prose that may merely contain one. Each embedded
 * URL substring is sanitized in place via {@link sanitizeUrl}; whatever GUID
 * or email shapes remain outside of a URL (e.g. an id interpolated directly
 * into an error message) are generalized the same way a URL path segment
 * would be.
 *
 * @param raw - The text to sanitize.
 * @returns The sanitized, length-bounded text.
 */
function sanitizeFreeText(raw: string): string {
  const withSanitizedUrls = raw.replace(URL_IN_TEXT_RE, (match) => sanitizeUrl(match));
  const withoutGuids = withSanitizedUrls.replace(GUID_RE, ":id");
  const withoutEmails = withoutGuids.replace(EMAIL_RE, ":redacted");
  return withoutEmails.slice(0, EXCEPTION_MESSAGE_LIMIT);
}

/**
 * Custom `SpanProcessor` that redacts identifying values from span
 * attributes before they leave the browser. Register this ahead of the
 * export-facing `BatchSpanProcessor` — processors run in registration order.
 *
 * Sanitizes in `onEnding`, not `onEnd`. This looks backwards at first read —
 * `onEnd(span: ReadableSpan)` is where the interface names the span as
 * mutable-looking (the cast to `Span` used to live here) — but the SDK's own
 * `Span.end()` sets `this._ended = true` *before* calling `onEnd`, and
 * `setAttribute` is a silent no-op once a span is ended
 * (`sdk-trace`'s `Span.js`: `if (value == null || this._isSpanEnded()) return this;`).
 * A mutation made from `onEnd` is discarded with no error, no warning, and no
 * indication in a unit test that stubs `setAttribute` rather than exercising
 * the real SDK class — which is exactly how this went unnoticed here.
 * `onEnding(span: Span)` fires immediately before that flag flips, which is
 * the SDK's actual extension point for this; the same call site prevents
 * anything else in this codebase from ever calling it after `onEnd` too.
 * It is marked experimental upstream (may change in a minor version, not
 * that it silently no-ops), which is a real caveat but a better one than
 * shipping sanitization that never ran.
 */
export class PrivacySanitizingSpanProcessor implements SpanProcessor {
  onStart(): void {
    // Nothing to sanitize yet: instrumentation libraries set most identifying
    // attributes (URLs, exception details) during the span's lifetime, not at
    // start. onEnding is where everything they set is visible at once, while
    // the span can still be mutated.
  }

  onEnding(span: Span): void {
    URL_ATTRIBUTES.forEach((key) => {
      const value = span.attributes[key];
      if (typeof value === "string") {
        span.setAttribute(key, sanitizeUrl(value));
      }
    });

    const exceptionMessage = span.attributes["exception.message"];
    if (typeof exceptionMessage === "string") {
      span.setAttribute("exception.message", sanitizeFreeText(exceptionMessage));
    }
  }

  onEnd(): void {
    // Sanitization already happened in onEnding, above, while the span was
    // still mutable. Required by the SpanProcessor interface regardless.
  }

  shutdown(): Promise<void> {
    return Promise.resolve();
  }

  forceFlush(): Promise<void> {
    return Promise.resolve();
  }
}
